import React, { useState, useEffect, useRef } from 'react';
import { Search, BarChart3, Package, Chrome, Zap, ShoppingCart, ChevronLeft, ChevronRight, Users, Video, Gift, Lightbulb } from 'lucide-react';
import Quotes from './Quotes';

const SellerContent = () => {
  const [circleScale, setCircleScale] = useState(0);
  const [currentFeatureIndex, setCurrentFeatureIndex] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      const communitySection = document.getElementById('community-seller');
      if (communitySection) {
        const rect = communitySection.getBoundingClientRect();
        const windowHeight = window.innerHeight;
        if (rect.top < windowHeight * 0.8 && rect.bottom > 0) {
          const scrollProgress = Math.max(0, Math.min(1, (windowHeight * 0.8 - rect.top) / (windowHeight * 0.5)));
          setCircleScale(scrollProgress);
        } else if (rect.top >= windowHeight * 0.8) {
          setCircleScale(0);
        } else {
          setCircleScale(1);
        }
      }
    };
    window.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const features = [
    { icon: Zap, title: "Live Scan", description: "Scan products in real-time across Amazon & Walmart & Ebay. Instant profit calculations and gated status." },
    { icon: Search, title: "Reverse Scan", description: "Search ASINs and find profitable deals across Amazon, Walmart & eBay with ungated finder." },
    { icon: ShoppingCart, title: "Storefront Scan", description: "Analyze competitor storefronts and discover their best-selling products to replicate success." },
    { icon: Package, title: "Wholesale Scan", description: "Bulk scan wholesale catalogs via CSV. Find high-demand, low-competition products instantly." },
    { icon: BarChart3, title: "Amazon to Amazon", description: "Flip products on Amazon. Track price drops and profit from strategic buying and selling." },
    { icon: BarChart3, title: "Amazon to Walmart", description: "Buy from Amazon, sell on Walmart. High Profit & ROI." },
    { icon: Chrome, title: "Chrome Ext. & Mobile Apps", description: "Extensions & Apps for instant product analysis on Amazon, Walmart, eBay and AIP." }
  ];

  const communityFeatures = [
    { icon: Users, title: "Thriving Community", description: "Join thousands of successful sellers sharing strategies, tips, and wins in our exclusive Discord/Facebook Communities" },
    { icon: Video, title: "Weekly Webinars", description: "Live training sessions where WE ask YOU what features to build next. Your voice shapes our roadmap" },
    { icon: Gift, title: "Free Additional Tools", description: "We provide many additional powerful tools at no extra cost, giving you maximum value at the lowest price!" },
    { icon: Lightbulb, title: "Ever-Expanding Toolset", description: "Continuously updated based on YOUR requests. New features added regularly based on community demand" }
  ];

  const nextFeature = () => setCurrentFeatureIndex((prev) => (prev + 1) % features.length);
  const prevFeature = () => setCurrentFeatureIndex((prev) => (prev - 1 + features.length) % features.length);

  return (
    <div>
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center px-6 pt-28 pb-20 md:pt-22 bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="hidden md:block absolute right-[-1%] top-[30%] -translate-y-1/2 opacity-5 pointer-events-none">
          <img src="logo512.png" alt="" className="w-96 h-96 md:w-[500px] md:h-[500px]" />
        </div>
        <div className="absolute left-[7%] top-[50%] -translate-y-1/2 opacity-5 pointer-events-none">
          <img src="logo512.png" alt="" className="w-96 h-96 md:w-[300px] md:h-[300px]" />
        </div>

        <div className="max-w-5xl mx-auto text-center relative z-10 xl:-mt-16 2xl:-mt-24">
          <h1 className="text-6xl md:text-7xl font-light mb-6 leading-tight text-gray-900">
            The <span className="font-normal italic bg-gradient-to-r from-teal-600 to-blue-600 bg-clip-text text-transparent">Toolset</span><br className="md:hidden" /> For <br className="hidden md:block" />
            Multi-Platform Sellers
          </h1>
          <p className="text-xl md:text-2xl text-gray-700 mb-8 max-w-3xl mx-auto font-light leading-relaxed">
            If you sell on Amazon, Walmart, eBay, or doing{' '}
            <span className="relative group inline-block">
              AIP
              <span className="absolute -right-2 text-teal-600 text-sm cursor-help">?</span>
              <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap">
                Amazon Influencer Program
              </span>
            </span>
            <br />
            congratulations, you're in the right place.
          </p>
          <div className="flex items-center justify-center gap-6 mb-6 mt-12">
            {['amazon', 'walmart', 'ebay', 'aip'].map((icon, i) => (
              <img key={icon} src={`/icons/${icon}.png`} alt={icon} className="h-12 w-12 object-contain"
                style={{ animation: 'float 6s ease-in-out infinite', animationDelay: `${i * 0.2}s` }} />
            ))}
          </div>
          <p className="text-xs text-gray-400 mb-6 flex items-center justify-center gap-1">
            Works across all Amazon marketplaces
            <img src="https://flagcdn.com/w20/us.png" alt="US" className="h-3" />
            <img src="https://flagcdn.com/w20/gb.png" alt="UK" className="h-3" />
            <img src="https://flagcdn.com/w20/ca.png" alt="CA" className="h-3" />
            <img src="https://flagcdn.com/w20/mx.png" alt="MX" className="h-3" />
          </p>
          <Quotes />
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12 md:mb-1">
            <button
              onClick={() => window.open('/communities', '_blank')}
              className="px-10 py-4 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-full text-lg font-medium hover:from-blue-700 hover:to-blue-800 transition transform hover:scale-105 shadow-xl flex items-center gap-3"
            >
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" /></svg>
              Facebook Community
            </button>
            <button
              onClick={() => window.open('/communities', '_blank')}
              className="px-10 py-4 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-full text-lg font-medium hover:from-indigo-700 hover:to-purple-700 transition transform hover:scale-105 shadow-xl flex items-center gap-3"
            >
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24"><path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515a.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0a12.64 12.64 0 0 0-.617-1.25a.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057a19.9 19.9 0 0 0 5.993 3.03a.078.078 0 0 0 .084-.028a14.09 14.09 0 0 0 1.226-1.994a.076.076 0 0 0-.041-.106a13.107 13.107 0 0 1-1.872-.892a.077.077 0 0 1-.008-.128a10.2 10.2 0 0 0 .372-.292a.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127a12.299 12.299 0 0 1-1.873.892a.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028a19.839 19.839 0 0 0 6.002-3.03a.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.956-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.955-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.946 2.418-2.157 2.418z" /></svg>
              Discord Community
            </button>
            <button
              onClick={() => window.location.href = '/signup'}
              className="px-10 py-4 bg-gradient-to-r from-teal-500 to-blue-600 text-white rounded-full text-lg font-medium hover:from-teal-600 hover:to-blue-700 transition transform hover:scale-105 shadow-xl flex items-center gap-3"
            >
              Click Here to Sign Up →
            </button>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="relative py-32 px-6 bg-gradient-to-br from-teal-900 via-teal-800 to-blue-900 text-white -mt-6">
        <svg className="absolute top-0 left-0 w-full" viewBox="0 0 1440 100" preserveAspectRatio="none" style={{ marginTop: '-1px' }}>
          <path d="M0,50 Q360,0 720,50 T1440,50 L1440,0 L0,0 Z" fill="url(#gradient-light)" />
          <defs>
            <linearGradient id="gradient-light" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" style={{ stopColor: '#F2F7FE', stopOpacity: 1 }} />
              <stop offset="100%" style={{ stopColor: 'rgb(239, 246, 255)', stopOpacity: 1 }} />
            </linearGradient>
          </defs>
        </svg>
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center mb-20">
            <p className="text-sm uppercase tracking-wider text-teal-300 mb-4">10+ multi-platform tools. 1 subscription.</p>
            <h2 className="text-5xl md:text-6xl font-light mb-6 text-white">
              The <span className="font-normal italic">Complete</span><br />
              <span className="font-normal">Community Toolset</span>
            </h2>
          </div>
          <div className="mb-20 max-w-5xl mx-auto">
            <div className="rounded-3xl overflow-hidden shadow-2xl bg-gradient-to-b from-white/20 to-white/10 p-2 backdrop-blur-sm border border-white/30">
              <div className="rounded-2xl overflow-hidden shadow-inner border border-white/20">
                <iframe width="100%" height="560" src="https://www.youtube.com/embed/vabuCn1-zNE" title="App Demo" frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen className="w-full" />
              </div>
            </div>
          </div>
          {/* Carousel */}
          <div className="max-w-5xl mx-auto mb-20">
            <div className="hidden md:block relative">
              <div className="overflow-hidden">
                <div className="flex gap-8 transition-transform duration-500 ease-out" style={{ transform: `translateX(-${currentFeatureIndex * 51.56}%)` }}>
                  {features.map((feature, idx) => (
                    <div key={idx} className="flex-shrink-0 w-[calc(50%-1rem)] flex items-start gap-6 p-8 rounded-3xl border border-white/10 hover:border-white/30 hover:bg-white/10 transition" style={{ background: 'rgba(255,255,255,0.05)', backdropFilter: 'blur(10px)' }}>
                      <div className="flex-shrink-0">
                        <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-teal-400 to-blue-400 flex items-center justify-center shadow-xl">
                          <feature.icon size={32} className="text-white" />
                        </div>
                      </div>
                      <div>
                        <h3 className="text-2xl font-light mb-3 text-white">{feature.title}</h3>
                        <p className="text-teal-100 leading-relaxed">{feature.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <button onClick={prevFeature} disabled={currentFeatureIndex === 0} className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-16 w-12 h-12 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white/20 transition disabled:opacity-30 border border-white/20">
                <ChevronLeft className="text-white" size={24} />
              </button>
              <button onClick={nextFeature} disabled={currentFeatureIndex >= features.length - 2} className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-16 w-12 h-12 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white/20 transition disabled:opacity-30 border border-white/20">
                <ChevronRight className="text-white" size={24} />
              </button>
            </div>
            <div className="flex justify-center gap-2 mt-8">
              {features.map((_, idx) => (
                <button key={idx} onClick={() => setCurrentFeatureIndex(idx)}
                  className={`h-2 rounded-full transition-all ${idx === currentFeatureIndex ? 'w-8 bg-teal-400' : 'w-2 bg-white/30 hover:bg-white/50'}`} />
              ))}
            </div>
          </div>
        </div>
        <svg className="absolute bottom-0 left-0 w-full" viewBox="0 0 1440 100" preserveAspectRatio="none" style={{ marginBottom: '-1px' }}>
          <path d="M0,50 Q360,100 720,50 T1440,50 L1440,100 L0,100 Z" fill="#F5F9FD" />
        </svg>
      </section>

      {/* Community Content Section */}
      <section className="relative py-32 px-6 bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-5xl md:text-6xl font-light mb-6 text-gray-900">
              Learn From The <span className="font-normal italic bg-gradient-to-r from-teal-600 to-blue-600 bg-clip-text text-transparent">Community</span>
            </h2>
            <p className="text-xl text-gray-700 max-w-3xl mx-auto">Weekly webinars, tutorials, success stories, and live training sessions</p>
          </div>
          <div className="max-w-5xl mx-auto mb-20">
            <div className="rounded-3xl overflow-hidden shadow-2xl border-2 border-gray-200">
              <iframe width="100%" height="500" src="https://www.youtube.com/embed/0d-eZEVcKRo" title="Webinar Recording" frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen className="w-full" />
            </div>
          </div>
          <div className="grid md:grid-cols-3 gap-8 mb-16">
            {[
              {
                gradient: 'from-red-500 to-red-600',
                icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />,
                title: 'Live Webinars',
                body: 'Join weekly live training sessions. Ask questions in real-time and learn the latest strategies.',
                extra: <div className="flex items-center gap-2 text-sm text-red-600 font-semibold"><span className="w-2 h-2 bg-red-600 rounded-full animate-pulse" />Next session: Wednesday <a className="text-blue-600 underline" target="_blank" rel="noopener noreferrer" href="/webinars">LINK TO JOIN</a></div>
              },
              {
                gradient: 'from-teal-500 to-blue-600',
                icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />,
                title: 'Video Tutorials',
                body: 'Step-by-step guides on every tool. From beginner basics to advanced techniques.'
              },
              {
                gradient: 'from-green-500 to-emerald-600',
                icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />,
                title: 'Success Stories',
                body: "Real results from real sellers. Learn what works from those who've made it."
              }
            ].map((card, i) => (
              <div key={i} className="rounded-3xl p-8 border border-gray-200 shadow-lg hover:shadow-xl transition" style={{ background: 'rgba(255,255,255,0.7)', backdropFilter: 'blur(10px)' }}>
                <div className={`w-16 h-16 bg-gradient-to-br ${card.gradient} rounded-2xl flex items-center justify-center mb-6`}>
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">{card.icon}</svg>
                </div>
                <h3 className="text-2xl font-light mb-3 text-gray-900">{card.title}</h3>
                <p className="text-gray-700 leading-relaxed mb-4">{card.body}</p>
                {card.extra}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Community First Section */}
      <section id="community-seller" className="relative py-32 px-6 bg-gradient-to-br from-teal-900 via-teal-800 to-blue-900 text-white">
        <svg className="absolute top-0 left-0 w-full" viewBox="0 0 1440 100" preserveAspectRatio="none" style={{ marginTop: '-1px' }}>
          <path d="M0,50 Q360,0 720,50 T1440,50 L1440,0 L0,0 Z" fill="#EFF6FE" />
        </svg>
        <div className="max-w-6xl mx-auto relative z-10">
          <div className="relative mb-20">
            <svg className="w-full max-w-4xl mx-auto opacity-20 transition-transform duration-700 ease-out" viewBox="0 0 800 400" style={{ transform: `scale(${0.5 + circleScale * 2})` }}>
              {[350, 300, 250, 200, 150].map((rx, i) => (
                <ellipse key={i} cx="400" cy="200" rx={rx} ry={rx * 0.43} fill="none" stroke="currentColor" strokeWidth="1" className="text-teal-300" style={{ opacity: circleScale * (0.3 + i * 0.15) }} />
              ))}
            </svg>
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center">
                <p className="text-sm text-teal-300 mb-4">Chat with the founders. Your voice matters.</p>
                <h2 className="text-6xl md:text-7xl font-light text-white">Community <span className="font-normal">first.</span></h2>
              </div>
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto mb-16">
            {communityFeatures.map((feature, idx) => (
              <div key={idx} className="flex items-start gap-6 p-8 rounded-3xl border border-white/10 hover:border-white/30 hover:bg-white/10 transition" style={{ background: 'rgba(255,255,255,0.05)', backdropFilter: 'blur(10px)' }}>
                <div className="flex-shrink-0">
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-teal-400 to-blue-400 flex items-center justify-center shadow-xl">
                    <feature.icon size={28} className="text-white" />
                  </div>
                </div>
                <div>
                  <h3 className="text-2xl font-light mb-3 text-white">{feature.title}</h3>
                  <p className="text-teal-100 leading-relaxed">{feature.description}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="text-center max-w-3xl mx-auto mb-6">
            <p className="text-xl text-teal-100 leading-relaxed">Your voice shapes our roadmap, your feedback drives innovation.</p>
          </div>
        </div>
        <svg className="absolute bottom-0 left-0 w-full" viewBox="0 0 1440 100" preserveAspectRatio="none" style={{ marginBottom: '-1px' }}>
          <path d="M0,50 Q360,100 720,50 T1440,50 L1440,100 L0,100 Z" fill="#F4F8FD" />
        </svg>
      </section>

      {/* Final CTA */}
      <section className="relative py-32 px-6 bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-5xl md:text-6xl font-light mb-8 text-gray-900 flex items-center justify-center gap-4 flex-wrap">
            Join <img src="/mspfull.png" alt="MultiSellerPro™" className="h-20 inline-block mt-4" /> today.
          </h2>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8">
            <button onClick={() => window.location.href = '/signup'} className="px-10 py-4 bg-gradient-to-r from-teal-500 to-blue-600 text-white rounded-full text-lg font-medium hover:from-teal-600 hover:to-blue-700 transition transform hover:scale-105 shadow-xl">
              Start Sourcing
            </button>
            <button onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} className="px-10 py-4 rounded-full text-lg font-medium text-gray-800 hover:bg-white/90 transition border border-gray-200 shadow-lg" style={{ background: 'rgba(255,255,255,0.7)', backdropFilter: 'blur(10px)' }}>
              Join the Communities
            </button>
          </div>
          <div className="flex items-center justify-center gap-6 mb-4">
            <a href="https://www.youtube.com/@MultiSellerPro" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-red-600 hover:text-red-400 transition-colors">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" /></svg>
              YouTube
            </a>
            <a href="https://x.com/MultiSellerPro" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-black hover:text-gray-400 transition-colors">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.744l7.737-8.835L1.254 2.25H8.08l4.253 5.622zm-1.161 17.52h1.833L7.084 4.126H5.117z" /></svg>
              X (Twitter)
            </a>
            <a href="https://www.instagram.com/multisellerpro" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-pink-600 hover:text-pink-400 transition-colors">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" /></svg>
              Instagram
            </a>
          </div>
          <p className="text-gray-600 text-lg">Need any help? Reach us anytime at <strong>contact@multisellerpro.com</strong></p>
        </div>
      </section>
    </div>
  );
};

export default SellerContent;
